import { useEffect, useRef, useState } from 'react';
import type { GenericUrlImportSource, GenericExtractionMethod } from '../../types';
import { VariantGroupingRuleEditor } from './VariantGroupingRuleEditor';

interface Props {
  source: GenericUrlImportSource;
  onChange: (updated: GenericUrlImportSource) => void;
}

const METHOD_OPTIONS: { value: GenericExtractionMethod; label: string; hint: string }[] = [
  { value: 'auto', label: 'Auto', hint: 'Try JSON-LD, then HTML specs, then AI.' },
  { value: 'json-ld', label: 'JSON-LD only', hint: 'Strict structured data.' },
  { value: 'html-specs', label: 'HTML specs only', hint: 'Selector-based parsing.' },
  { value: 'ai', label: 'AI only', hint: 'LLM extraction — slowest, most flexible.' },
];

function parseLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith('#'));
}

// Controlled textarea bound to a string[] in the parent. Stores the raw
// textarea string locally so the user can type empty lines (e.g. press Enter
// to start a new URL) without parseLines stripping the trailing newline on
// every keystroke. The parsed array is pushed to the parent on blur.
function UrlListTextarea({
  value,
  onCommit,
  rows,
  placeholder,
}: {
  value: string[];
  onCommit: (next: string[]) => void;
  rows: number;
  placeholder: string;
}) {
  const [raw, setRaw] = useState(() => value.join('\n'));
  const lastCommittedRef = useRef(value);

  // Re-sync from props when the parent's value changes from elsewhere
  // (e.g. switching brands). Skip the resync if the change is the one we
  // just committed ourselves, so the user's in-progress trailing newlines
  // don't get stripped.
  useEffect(() => {
    if (value === lastCommittedRef.current) return;
    setRaw(value.join('\n'));
    lastCommittedRef.current = value;
  }, [value]);

  return (
    <textarea
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        const parsed = parseLines(raw);
        const prev = lastCommittedRef.current;
        const same =
          parsed.length === prev.length && parsed.every((u, i) => u === prev[i]);
        if (!same) {
          lastCommittedRef.current = parsed;
          onCommit(parsed);
        }
      }}
      placeholder={placeholder}
      rows={rows}
      className="w-full text-sm border border-gray-300 rounded px-2 py-1 font-mono"
    />
  );
}

export function GenericSourceEditor({ source, onChange }: Props) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2 leading-relaxed">
        Tell us where to find this brand&apos;s watches. You can give us
        collection / listing pages to crawl, direct product page URLs, or both —
        we&apos;ll merge and dedupe them.
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Collection / listing pages to crawl
        </label>
        <UrlListTextarea
          value={source.collectionPageUrls}
          onCommit={(next) => onChange({ ...source, collectionPageUrls: next })}
          rows={3}
          placeholder={'https://brand.com/collections/all\nhttps://brand.com/collections/dive'}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          e.g. a brand&apos;s &ldquo;All Watches&rdquo; page. We&apos;ll scrape
          every product link we find here. One URL per line.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Direct product page URLs
        </label>
        <UrlListTextarea
          value={source.productUrls}
          onCommit={(next) => onChange({ ...source, productUrls: next })}
          rows={4}
          placeholder={'https://brand.com/products/reference-123\nhttps://brand.com/products/reference-456'}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          One product page URL per line. Use this when there&apos;s no listing
          page, or to add specific watches a crawl misses. Lines starting with
          # are ignored.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Extraction method
        </label>
        <select
          value={source.extractionMethod}
          onChange={(e) =>
            onChange({
              ...source,
              extractionMethod: e.target.value as GenericExtractionMethod,
            })
          }
          className="text-sm border border-gray-300 rounded px-2 py-1"
        >
          {METHOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-gray-400 mt-1">
          {METHOD_OPTIONS.find((o) => o.value === source.extractionMethod)?.hint}
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          JSON listing URL (optional)
        </label>
        <input
          type="text"
          value={source.jsonListingUrl ?? ''}
          onChange={(e) =>
            onChange({ ...source, jsonListingUrl: e.target.value.trim() || undefined })
          }
          placeholder="https://brand.com/collections/…/search_result.search.json?page=1"
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 font-mono"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          If the collection page is JS-rendered and exposes a JSON endpoint,
          paste it here. Overrides HTML crawling — product URLs are read from the
          JSON (pagination handled automatically).
        </p>
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
          CSS selector overrides (advanced)
        </summary>
        <div className="mt-2 space-y-3 pl-3">
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Collection / listing page selectors
            </p>
            <SelectorField
              name="productLink"
              label="productLink"
              value={source.cssSelectorOverrides?.productLink}
              placeholder="a.product-card__link"
              help="Finds the product links to crawl on a listing page. Authoritative — when set, the automatic link detection is skipped entirely, so if it matches nothing the crawl returns zero URLs rather than falling back."
              onChange={(next) => setOverride(source, onChange, 'productLink', next)}
            />
          </div>

          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Product detail page selectors
            </p>
            <SelectorField
              name="image"
              label="image"
              value={source.cssSelectorOverrides?.image}
              placeholder="img.wp-post-image"
              help="Picks the main product image on a product page. Point it at the <img> itself or a container holding it. Beats the automatic image sources (structured data, og:image, page scan); if it matches nothing on a given page, that page falls back to them."
              onChange={(next) => setOverride(source, onChange, 'image', next)}
            />
          </div>
        </div>
      </details>
      <VariantGroupingRuleEditor
        rule={source.variantGroupingRule}
        onChange={(variantGroupingRule) => onChange({ ...source, variantGroupingRule })}
      />
    </div>
  );
}

type OverrideKey = 'productLink' | 'image';

/**
 * Write one selector override back onto the source. The key is DELETED rather
 * than set to undefined when cleared: Firestore rejects undefined values and
 * saveBrandImportSources' stripUndefined only walks the top level, so a stale
 * `{ image: undefined }` made "Save changes" fail outright.
 */
function setOverride(
  source: GenericUrlImportSource,
  onChange: (updated: GenericUrlImportSource) => void,
  key: OverrideKey,
  value: string,
) {
  const next = { ...(source.cssSelectorOverrides ?? {}) };
  // Stored as typed, NOT trimmed: a descendant selector needs its spaces, and
  // trimming a controlled input on every keystroke makes ".gallery img"
  // impossible to type. Consumers trim at the point of use.
  if (value.trim()) {
    next[key] = value;
  } else {
    delete next[key];
  }
  onChange({
    ...source,
    cssSelectorOverrides: Object.keys(next).length > 0 ? next : undefined,
  });
}

function SelectorField({
  name,
  label,
  value,
  placeholder,
  help,
  onChange,
}: {
  name: string;
  label: string;
  value?: string;
  placeholder: string;
  help: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label
        htmlFor={`selector-${name}`}
        className="block text-[11px] font-mono text-gray-600 mb-0.5"
      >
        {label}
      </label>
      <input
        id={`selector-${name}`}
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs border border-gray-300 rounded px-2 py-1 font-mono"
      />
      <p className="text-[11px] text-gray-400 mt-1">{help}</p>
    </div>
  );
}
